import { useCallback } from "react";
import dayjs from "dayjs";

import {fetchLeaveRequestByEmployeeId, createLeaveRequest,deleteLeaveRequest} from "../data/redux/attendance/leave/leaveAction";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../data/redux/store";

export const useAILeaveActions = (
  employeeId: number,
  employeeassignleave: any[],
  employeeLeaveRequest: any[]
) => {
  const dispatch = useDispatch<AppDispatch>();

  // 2️⃣ Apply Leave
  const handleAIApplyLeave = useCallback(
    async (leaveType: string, fromDate?: string, toDate?: string, days?: number) => {

      const found = employeeassignleave.find(
        (l) => l.LeaveTypeName.toLowerCase() === leaveType.toLowerCase()
      );

      if (!found) {
        window.dispatchEvent(new CustomEvent("ai-chat", { detail: `Leave type '${leaveType}' not found` }));
        return;
      }

      let start = null, end = null;

      if (fromDate && toDate) {
        start = dayjs(fromDate);
        end = dayjs(toDate);
      } else if (days) {
        start = dayjs();
        end = start.add(days - 1, "day");
      } else {
        window.dispatchEvent(new CustomEvent("ai-chat", { detail: "Please specify date(s) to apply leave." }));
        return;
      }

      const duration = end.diff(start, "day") + 1;

      const payload = {
        EmployeeId: employeeId,
        LeaveTypeId: found.LeaveTypeId,
        FromDate: start.format("YYYY-MM-DD"),
        ToDate: end.format("YYYY-MM-DD"),
        Duration: duration,
        LeaveMode: "Full Day",
        LeaveRequestStatus: "Pending",
        Description: `AI applied ${leaveType} for ${duration} days`,
      };
      window.dispatchEvent(new Event("ai-loader-start"));
      const res = await dispatch(createLeaveRequest(payload));
      if (res.meta.requestStatus === "fulfilled") {
        window.dispatchEvent(new Event("ai-loader-stop"));
        window.dispatchEvent(new CustomEvent("ai-chat", { detail: `✓ Leave applied successfully!` }));
        dispatch(fetchLeaveRequestByEmployeeId(employeeId));
      } else {
        window.dispatchEvent(new Event("ai-loader-stop"));
        window.dispatchEvent(new CustomEvent("ai-chat", { detail: `⚠ Could not apply leave` }));
      }
    },
    [employeeId, employeeassignleave, dispatch]
  );

  // 4️⃣ Cancel ALL future leaves
const handleAICancelAllLeaves = useCallback(async () => {

  const today = dayjs();

  const futureLeaves = employeeLeaveRequest.filter((l: any) =>
    dayjs(l.FromDate).isSameOrAfter(today, "day")
  );

  if (!futureLeaves.length) {
    return window.dispatchEvent(
      new CustomEvent("ai-chat", { detail: "You have no upcoming leaves to cancel." })
    );
  }

  window.dispatchEvent(new Event("ai-loader-start"));

  for (const leave of futureLeaves) {

    // PENDING → delete directly
    if (leave.LeaveRequestStatus === "Pending") {
      await dispatch(deleteLeaveRequest(leave.Id));
      continue;
    }

    // APPROVED → Create cancellation request
    if (leave.LeaveRequestStatus === "Approved") {
      const payload = {
        LeaveTypeId: leave.LeaveTypeId,
        LeaveTypeName: leave.LeaveType,
        LeaveId: leave.Id,
        LeaveMode: leave.LeaveMode,
        RemainingDays: leave.RemainingDays || "",
        CancellationReason: "AI auto cancel all upcoming leave",
        Cancellation_status: "Pending",
        EmployeeId: leave.EmployeeId,
        LeaveRequestStatus: leave.LeaveRequestStatus,
        FromDate: leave.FromDate,
        ToDate: leave.ToDate,
        Duration: leave.Duration,
      };

      await dispatch(createLeaveRequest(payload));
    }
  }

  window.dispatchEvent(new Event("ai-loader-stop"));

  window.dispatchEvent(
    new CustomEvent("ai-chat", {
      detail: "✓ All your upcoming leaves have been cancelled.",
    })
  );

  dispatch(fetchLeaveRequestByEmployeeId(employeeId));

}, [employeeLeaveRequest, employeeId, dispatch]);



  // 3️⃣ Cancel Leave
 const handleAICancelLeave = useCallback(async () => {

  if (!employeeLeaveRequest.length) {
    return window.dispatchEvent(
      new CustomEvent("ai-chat", { detail: "You have no leave to cancel." })
    );
  }

  // Get latest upcoming leave
  const upcoming = employeeLeaveRequest
    .filter((l: any) => dayjs(l.FromDate).isSameOrAfter(dayjs(), "day"))
    .sort((a: any, b: any) => new Date(a.FromDate).getTime() - new Date(b.FromDate).getTime())[0];

  if (!upcoming) {
    return window.dispatchEvent(
      new CustomEvent("ai-chat", { detail: "No future leave found to cancel." })
    );
  }

  const leave = upcoming;
  const today = dayjs();

  // ❌ Past leave
  if (dayjs(leave.FromDate).isBefore(today, "day")) {
    return window.dispatchEvent(
      new CustomEvent("ai-chat", { detail: "Past leave cannot be cancelled." })
    );
  }

  //---------------------------------------------------------
  // 1️⃣ PENDING FUTURE LEAVE → Delete using existing function
  //---------------------------------------------------------
  if (leave.LeaveRequestStatus === "Pending") {
    window.dispatchEvent(new Event("ai-loader-start"));

    const res = await dispatch(deleteLeaveRequest(leave.Id));

    window.dispatchEvent(new Event("ai-loader-stop"));

    if (res.meta.requestStatus === "fulfilled") {
      window.dispatchEvent(
        new CustomEvent("ai-chat", {
          detail: "✓ Your pending leave has been cancelled successfully.",
        })
      );
      dispatch(fetchLeaveRequestByEmployeeId(employeeId));
    } else {
      window.dispatchEvent(
        new CustomEvent("ai-chat", {
          detail: "⚠ Unable to cancel pending leave.",
        })
      );
    }
    return;
  }

  //---------------------------------------------------------
  // 2️⃣ APPROVED FUTURE LEAVE → Create cancellation request
  //---------------------------------------------------------
  if (leave.LeaveRequestStatus === "Approved") {
    const payload = {
      LeaveTypeId: leave.LeaveTypeId,
      LeaveTypeName: leave.LeaveType,
      LeaveId: leave.Id,
      LeaveMode: leave.LeaveMode,
      RemainingDays: leave.RemainingDays || "",
      CancellationReason: "AI auto cancellation request",
      Cancellation_status: "Pending",
      EmployeeId: leave.EmployeeId,
      LeaveRequestStatus: leave.LeaveRequestStatus,
      FromDate: leave.FromDate,
      ToDate: leave.ToDate,
      Duration: leave.Duration,
    };

    window.dispatchEvent(new Event("ai-loader-start"));
    const res: any = await dispatch(createLeaveRequest(payload));
    window.dispatchEvent(new Event("ai-loader-stop"));

    if (res.meta.requestStatus === "fulfilled") {
      window.dispatchEvent(
        new CustomEvent("ai-chat", {
          detail: "✓ Cancellation request submitted for your approved leave.",
        })
      );
      dispatch(fetchLeaveRequestByEmployeeId(employeeId));
    } else {
      window.dispatchEvent(
        new CustomEvent("ai-chat", {
          detail: "⚠ Failed to create cancellation request.",
        })
      );
    }
    return;
  }

  // Fallback
  window.dispatchEvent(
    new CustomEvent("ai-chat", {
      detail: "Unable to cancel this leave.",
    })
  );

}, [employeeLeaveRequest, dispatch, employeeId]);

  return {
    handleAIApplyLeave,
    handleAICancelLeave,
    handleAICancelAllLeaves
  };
};


